import type { PixelEvent } from '@/events';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { BufferFlushReason } from '../../contracts/buffer';
import type {
  SessionSummaryData,
  SessionSummaryReason,
  SessionSummarySnapshot,
} from '../../contracts/sessionSummary';
import type { RateLimitSnapshot } from '../../contracts/rateLimit';
import type { BufferSnapshot } from '../../contracts/buffer';
import type { StateMachineSnapshot } from '../../contracts/stateMachine';
import type {
  BehaviorSignal,
  BehaviorSignalBus,
  BehaviorSignalType,
} from '../../contracts/signals';
import type { NormalizedRuntimeIntelligenceConfig } from '../../resolveRuntimeIntelligenceConfig';

import { BehaviorBuffer } from './BehaviorBuffer';
import { BehaviorRateLimiter } from './BehaviorRateLimiter';
import { BehaviorStateMachine } from './BehaviorStateMachine';
import { SessionSummaryAccumulator } from './SessionSummaryAccumulator';

const CONSUMED_SIGNALS: readonly BehaviorSignalType[] = [
  'HEARTBEAT',
  'SCROLL',
  'CLICK',
  'RAGE_CLICK',
  'IDLE',
  'ACTIVITY',
  'VISIBLE',
  'HIDDEN',
  'EXIT_INTENT',
  'VIEWPORT_ENTER',
  'VIEWPORT_EXIT',
];

export type EngagementScoreProvider = () => {
  readonly score: number;
  readonly peakScore: number;
} | null;

export type BehaviorRuntimeCoordinatorOptions = {
  readonly config: NormalizedRuntimeIntelligenceConfig;
  readonly getEngagement: EngagementScoreProvider;
};

/**
 * Coordena o pipeline de Runtime Intelligence (Etapa 20):
 * SignalBus → RateLimiter → Buffer → StateMachine → SessionSummaryAccumulator →
 * BEHAVIOR_SESSION_SUMMARY. Isola falhas de cada componente para não impedir o
 * destroy. Não persiste eventos, não chama Transport, funciona no-op em SSR.
 */
export class BehaviorRuntimeCoordinator {
  readonly id = 'runtimeIntelligence';

  private readonly context: PixelRuntimeContext;

  private readonly config: NormalizedRuntimeIntelligenceConfig;

  private signalBus: BehaviorSignalBus | null = null;

  private readonly getEngagement: EngagementScoreProvider;

  private readonly rateLimiter: BehaviorRateLimiter;

  private readonly buffer: BehaviorBuffer;

  private readonly stateMachine: BehaviorStateMachine;

  private readonly accumulator = new SessionSummaryAccumulator();

  private unsubscribers: Array<() => void> = [];

  private initialized = false;

  private running = false;

  private destroyed = false;

  private startedAt: number | undefined;

  private emittedCount = 0;

  private lastEmittedAt: number | undefined;

  private hiddenSummaryEmitted = false;

  constructor(
    context: PixelRuntimeContext,
    options: BehaviorRuntimeCoordinatorOptions
  ) {
    this.context = context;
    this.config = options.config;
    this.getEngagement = options.getEngagement;
    this.rateLimiter = new BehaviorRateLimiter(options.config.rateLimit);
    this.buffer = new BehaviorBuffer(options.config.buffer);
    this.stateMachine = new BehaviorStateMachine(options.config.stateMachine);
  }

  init(signalBus: BehaviorSignalBus): void {
    if (this.initialized && !this.destroyed) return;

    const now = Date.now();
    this.signalBus = signalBus;
    this.destroyed = false;
    this.startedAt = now;
    this.hiddenSummaryEmitted = false;
    this.rateLimiter.reset();
    this.buffer.reset();
    this.accumulator.reset(now);
    // reinit cria nova geração (a state machine persiste entre init/destroy).
    this.stateMachine.beginInit(now);

    this.bindSignals();

    this.initialized = true;
    this.running = true;
  }

  destroy(): void {
    if (this.destroyed && !this.initialized) return;

    const wasRunning = this.running;
    this.running = false;
    this.destroyed = true;

    this.unbindSignals();

    const now = Date.now();

    this.safe(() => this.stateMachine.beginDestroy(now));

    if (this.config.buffer.enabled) {
      this.safe(() => this.drainBufferToAccumulator('destroy', now));
    }

    if (wasRunning && this.config.sessionSummary.emitOnDestroy) {
      this.safe(() => this.emitSummary('destroy', now));
    }

    this.safe(() => this.stateMachine.finishDestroy(now));

    this.signalBus = null;
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  isRunning(): boolean {
    return this.running;
  }

  /** @internal Flush manual do buffer (playground/testes). */
  flushNow(): void {
    if (!this.running || this.destroyed) return;
    if (!this.config.buffer.enabled) return;
    this.drainBufferToAccumulator('manual', Date.now());
  }

  /** @internal Emissão manual de summary (playground/testes). */
  emitSummaryNow(reason: SessionSummaryReason = 'hidden'): boolean {
    if (!this.running || this.destroyed) return false;
    const now = Date.now();
    if (this.config.buffer.enabled) {
      this.drainBufferToAccumulator('manual', now);
    }
    return this.emitSummary(reason, now);
  }

  getRateLimitSnapshot(): RateLimitSnapshot {
    return this.rateLimiter.getSnapshot();
  }

  getBufferSnapshot(): BufferSnapshot {
    return this.buffer.getSnapshot();
  }

  getStateMachineSnapshot(): StateMachineSnapshot {
    return this.stateMachine.getSnapshot();
  }

  getSessionSummarySnapshot(): SessionSummarySnapshot {
    return {
      enabled: this.config.sessionSummary.enabled,
      emitOnHidden: this.config.sessionSummary.emitOnHidden,
      emitOnDestroy: this.config.sessionSummary.emitOnDestroy,
      minimumSessionMs: this.config.sessionSummary.minimumSessionMs,
      emittedCount: this.emittedCount,
      lastEmittedAt: this.lastEmittedAt,
      startedAt: this.startedAt,
    };
  }

  private bindSignals(): void {
    this.unbindSignals();
    const bus = this.signalBus;
    if (!bus) return;
    for (const type of CONSUMED_SIGNALS) {
      this.unsubscribers.push(
        bus.subscribe(type, signal => this.handleSignal(signal))
      );
    }
  }

  private unbindSignals(): void {
    for (const unsub of this.unsubscribers) {
      try {
        unsub();
      } catch {
        // ignore
      }
    }
    this.unsubscribers = [];
  }

  private handleSignal(signal: BehaviorSignal): void {
    if (this.destroyed || !this.running) return;

    try {
      const now = signal.timestamp;

      const decision = this.rateLimiter.evaluate(signal.type, now);
      if (decision === 'dropped') return;

      if (this.config.stateMachine.enabled) {
        this.stateMachine.applySignal(signal.type, now);
      }

      if (this.config.buffer.enabled) {
        this.buffer.add(signal, now);
        if (this.buffer.shouldFlushBySize()) {
          this.drainBufferToAccumulator('size', now);
        } else if (this.buffer.hasAgedEntries(now)) {
          this.drainBufferToAccumulator('age', now);
        }
      } else if (this.config.sessionSummary.enabled) {
        // Sem buffer, o accumulator é alimentado diretamente.
        this.accumulator.ingest(signal);
      }

      if (signal.type === 'HIDDEN') {
        this.onHidden(now);
      } else if (signal.type === 'VISIBLE' || signal.type === 'ACTIVITY') {
        this.hiddenSummaryEmitted = false;
      }
    } catch {
      // Isolation: falha no pipeline de um sinal não derruba o runtime.
      this.context.logger.warn(
        '[Pixel] Behavior runtime pipeline error (isolated)'
      );
    }
  }

  private onHidden(now: number): void {
    if (this.config.buffer.enabled && this.config.buffer.flushOnHidden) {
      this.drainBufferToAccumulator('hidden', now);
    }
    if (
      this.config.sessionSummary.enabled &&
      this.config.sessionSummary.emitOnHidden &&
      !this.hiddenSummaryEmitted
    ) {
      if (this.emitSummary('hidden', now)) {
        this.hiddenSummaryEmitted = true;
      }
    }
  }

  private drainBufferToAccumulator(
    reason: BufferFlushReason,
    now: number
  ): void {
    const entries = this.buffer.flush(reason, now);
    if (!this.config.sessionSummary.enabled) return;
    for (const entry of entries) {
      for (let i = 0; i < entry.count; i += 1) {
        this.accumulator.ingest(entry.signal);
      }
    }
  }

  private emitSummary(reason: SessionSummaryReason, now: number): boolean {
    if (!this.config.sessionSummary.enabled) return false;

    const startedAt = this.startedAt ?? now;
    if (
      this.config.sessionSummary.minimumSessionMs > 0 &&
      now - startedAt < this.config.sessionSummary.minimumSessionMs
    ) {
      return false;
    }

    this.accumulator.flush(now);
    const state = this.accumulator.getState();
    const engagement = this.getEngagement() ?? { score: 0, peakScore: 0 };
    const finalState = this.stateMachine.getState();
    const generation = this.stateMachine.getGeneration();

    const data: SessionSummaryData = {
      activeDurationMs: state.activeDurationMs,
      idleDurationMs: state.idleDurationMs,
      visibleDurationMs: state.visibleDurationMs,
      hiddenDurationMs: state.hiddenDurationMs,
      maxScrollPercent: state.maxScrollPercent,
      clickCount: state.clickCount,
      rageClickCount: state.rageClickCount,
      viewportEntryCount: state.viewportEntryCount,
      viewportVisibleDurationMs: state.viewportVisibleDurationMs,
      heartbeatCount: state.heartbeatCount,
      engagementScore: engagement.score,
      engagementPeakScore: engagement.peakScore,
      startedAt,
      endedAt: now,
      finalState,
    };

    try {
      const event = this.context.services.eventBuilder.build({
        type: 'BEHAVIOR_SESSION_SUMMARY',
        category: 'ENGAGEMENT',
        ...data,
        generation,
        reason,
      }) as PixelEvent;

      this.context.services.dispatcher.dispatch(event);
      this.emittedCount += 1;
      this.lastEmittedAt = now;
      return true;
    } catch {
      // Best-effort: conta como emitido para garantir "no máximo uma vez".
      this.emittedCount += 1;
      this.lastEmittedAt = now;
      this.context.logger.warn(
        '[Pixel] Behavior session summary emit failed (listener isolated)'
      );
      return true;
    }
  }

  private safe(fn: () => void): void {
    try {
      fn();
    } catch {
      this.context.logger.warn(
        '[Pixel] Behavior runtime coordinator step failed (isolated)'
      );
    }
  }
}
